import { useSelector } from 'react-redux';

import { Box, Grid } from '@mui/material';

import { TextField, DatePicker } from 'mui-rff';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { Typography } from '@components/styledComponents';
import MultiSelect from '@components/MultiSelect';

import { selectIsUserAdmin } from '@store/auth';
import { selectCurrencies } from '../../../../payment-options/store/payment-options-currencies';
import { selectRuleSources } from '../../../store/orchestration-rules-sources.slice';

import { getStatusCustomColumn } from '../../rule-list/PaymentOrchestrationRules';
import CollectedCommission from './CollectedCommission';

export default function DetailsForm({ id, isEdit = false, values }) {
  const sources = useSelector(selectRuleSources);
  const isAdminUser = useSelector(selectIsUserAdmin);
  const currencies = useSelector(selectCurrencies);

  return (
    <>
      <Typography
        className="form-header"
        variant="formHeader"
        style={{ marginTop: 10 }}>
        Details
      </Typography>
      {isEdit && (
        <Box style={{ marginBottom: 30 }}>
          <Typography variant="body1">
            Status: {getStatusCustomColumn(values.status)}
          </Typography>
        </Box>
      )}
      <Grid
        container
        maxWidth="lg"
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={2}>
        <Grid
          item
          xs={12}
          md={3}>
          <MultiSelect
            id="source"
            name="source"
            label="Source"
            required
            readOnly={isEdit}
            items={sources}
            multiple={false}
            optionDisplayKey="source"
            optionValueKey="source"
            value={values.source}
            isOptionEqualToValue={(option, value) => option.source === value}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={3}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Effectivity Start Date (DD/MM/YYYY)"
              name="effectiveStartDate"
              format="DD/MM/YYYY"
              required
              // TODO: remove once update implem is finalized
              readOnly={isEdit}
              minDate={!isEdit ? dayjs().add(1, 'day') : null}
              slotProps={{
                field: { fullWidth: true },
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid
          item
          xs={12}
          md={3}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Effectivity End Date (DD/MM/YYYY)"
              name="effectiveEndDate"
              format="DD/MM/YYYY"
              required
              // TODO: remove once update implem is finalized
              readOnly={isEdit}
              minDate={!isEdit ? dayjs().add(1, 'day') : null}
              disablePast={!isEdit}
              slotProps={{
                field: { fullWidth: true },
              }}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>
      <Grid
        container
        maxWidth="lg"
        style={{ marginTop: 8 }}
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={2}>
        <Grid
          item
          xs={4}
          md={3}>
          <MultiSelect
            label="Monthly Cap Currency"
            name="settlementCurrency"
            key="settlementCurrency"
            items={currencies}
            required
            readOnly={isEdit}
            multiple={false}
            optionDisplayKey="code"
            optionValueKey="code"
            value={values.settlementCurrency}
            isOptionEqualToValue={(option, value) => option.code === value}
            customOptionLabel={(option) => `${option.name} (${option.code})`}
            customFilterStringify={(option) =>
              `${option.name} (${option.code})`
            }
          />
        </Grid>
        <Grid
          item
          xs={4}
          md={3}>
          <TextField
            name="monthlyCap"
            label={`Monthly Cap (in ${
              values.settlementCurrency
                ? values.settlementCurrency
                : 'Monthly Cap Currency'
            })`}
            required
            type="number"
            InputProps={{
              readOnly: isEdit,
              inputProps: { min: 0 },
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid
          item
          xs={4}
          md={3}>
          <CollectedCommission
            id={id}
            values={values}
            isEdit={isEdit}
          />
        </Grid>
      </Grid>
      <Grid
        container
        maxWidth="lg"
        style={{ marginTop: 8 }}
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={2}>
        <Grid
          item
          xs={12}
          md={3}>
          <TextField
            name="transactionRatio"
            label="Transaction Routing Ratio (in %)"
            type="number"
            required
            InputProps={{
              readOnly: isEdit,
              inputProps: { min: 0, max: 100 },
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        {isAdminUser && isEdit && (
          <Grid
            item
            xs={12}
            md={6}>
            <TextField
              name="channelHash"
              label="Channel Hash"
              required
              InputProps={{ readOnly: true }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
        )}
      </Grid>
    </>
  );
}
